import React from 'react';
import {
  Cell,
  Pie,
  PieChart,
  Legend,
  Tooltip,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Sector,
} from 'recharts';
import { useTheme } from '@material-ui/core/styles';
import { lightenColor, spaceRemover, toColor } from 'util/string';
import { fetchChartData } from 'services/dynamicjson';
import { TableList } from 'components/Common/TableList';
import { deeplinkToURL } from 'services/deeplink';

// TODO take this gradient to separate GraphToolKit library. (Separation)
const linearGradients = {
  gradient1: {
    // red above blue down
    color1: '#BC2A13',
    color2: '#2896E9',
  },
  gradient2: {
    // green above blue down
    color1: '#1ED2AC',
    color2: '#2896E9',
  },
  gradient3: {
    // purple above blue down
    color1: '#7467F0',
    color2: '#2896E9',
  },
  gradient4: {
    color1: '#2896E9',
    color2: '#C272FF',
  },
};

const RADIAN = Math.PI / 180;

function PieChartWidget({
  chartData,
  dataKeys,
  nameKey,
  colors,
  width,
  height,
  showLabels,
  ...props
}) {
  const theme = useTheme();
  nameKey = nameKey || 'name';
  dataKeys = dataKeys || ['value'];
  const pieFill = (colors && colors.default) || theme.palette.primary.main;
  const fillFor = (name) => (colors && colors[name]) || toColor(name);
  const [activeIndex, setActiveIndex] = React.useState(null);
  const { handleDeeplink, setDeepLink, overrideHandleDeepLink } = props;

  const renderActiveShape = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle } =
      props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const mx = cx + (outerRadius + 30) * cos;
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={`${colors[props.name]}`}
          cursor="pointer"
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          fill={`${colors[props.name]}66`}
          cursor="pointer"
        />
      </g>
    );
  };

  const customLabel = ({ x, y, value, label, ...props }) => {
    const { cx, cy, midAngle, outerRadius, name } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';
    return (
      <>
        {value ? (
          <g>
            <path
              d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
              stroke="#394A58"
              fill="none"
            />
            <circle cx={ex} cy={ey} r={2} fill="#394A58" stroke="#394A58" />
            <text
              x={ex + (cos >= 0 ? 1 : -1) * 12}
              y={ey}
              textAnchor={textAnchor}
              fill="#777"
              fontSize="15px"
              fontWeight="bolder"
            >
              {name}
            </text>
            <text
              x={ex + (cos >= 0 ? 1 : -1) * 12}
              y={ey}
              dy={18}
              textAnchor={textAnchor}
              fill="#394a58"
              fontWeight="bolder"
              fontSize="17px"
            >
              {label || value}
            </text>
          </g>
        ) : null}
      </>
    );
  };

  const customLabelPlusActiveShape = (props) => {
    return [customLabel(props), renderActiveShape(props)];
  };

  const changeIndex = (data, index) => {
    setActiveIndex(index);
  };

  return (
    <div
      style={{
        paddingRight: '30px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <ResponsiveContainer width="95%" height={375}>
        <PieChart width={width} height={height}>
          <defs>
            {Object.keys(colors).map((gradient, index) => (
              <linearGradient
                id={`${spaceRemover(gradient)}-${index}`}
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
                spreadMethod="reflect"
              >
                <stop offset="0" stopColor={colors[gradient]} />
                <stop
                  offset="1"
                  stopColor={lightenColor(colors[gradient], 100)}
                />
              </linearGradient>
            ))}
          </defs>
          <Legend
            payload={
              !showLabels &&
              (chartData || []).map((item, index) => ({
                id: item.name,
                type: 'circle',
                value: `${item.name} (${item.label})`,
                color: `url(#${spaceRemover(item[nameKey])}-${index})`,
              }))
            }
            iconType="circle"
          />
          {dataKeys.map((dataKey, index) => (
            <Pie
              activeIndex={activeIndex}
              activeShape={
                showLabels ? renderActiveShape : customLabelPlusActiveShape
              }
              isAnimationActive={false}
              data={chartData}
              dataKey={dataKey}
              onClick={(sectorProps) => {
                return (
                  sectorProps.deep_link &&
                  (overrideHandleDeepLink ||
                    !sectorProps.apiType ||
                    sectorProps.apiType === 1
                    ? setDeepLink
                    : handleDeeplink)(
                    sectorProps.deep_link,
                    sectorProps.apiType,
                    props
                  )
                );
              }}
              nameKey={nameKey}
              fill={pieFill}
              innerRadius={70}
              outerRadius={115 + 30 * index}
              labelLine={false}
              label={showLabels ? customLabel : null}
              paddingAngle={1}
              onMouseEnter={changeIndex}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {chartData &&
                chartData.map((item, index) => (
                  <Cell
                    key={item[nameKey]}
                    fill={`url(#${spaceRemover(item[nameKey])}-${index})`}
                  />
                ))}
            </Pie>
          ))}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
const getLableValue = (label, data, _key) => {
  return ((data || []).find((ele) => ele.name === label) || {})[_key].label;
};

function CustomBarToolTip(props) {
  const { active } = props;
  if (active) {
    const { payload, label, tooltipData } = props;
    const filterdPayload = payload.filter(
      (data) => data.dataKey !== 'deep_link' && data.dataKey !== 'apiType'
    );

    return (
      <div
        style={{
          background: '#11193f 0% 0% no-repeat padding-box',
          boxShadow: '0px 5px 10px #CBD1DF80',
          borderRadius: '4px',
          padding: '20px 10px 10px 20px',
        }}
      >
        <p
          style={{
            fontSize: '18px',
            textTransform: 'uppercase',
            fontWeight: 'bolder',
            color: '#edeef1',
            letterSpacing: 1,
            margin: -3,
          }}
        >
          {label}
        </p>
        {filterdPayload.map((item) => (
          <p>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 'bolder',
                color: '#edeef1',
              }}
            >
              {getLableValue(label, tooltipData, item.name)}&nbsp;&nbsp;
            </span>
            <span
              style={{
                color: '#bcbed2',
                fontSize: 14,
                fontWeight: 'bolder',
              }}
            >
              {item.name}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

function BarChartWidget({
  chartData,
  dataKeys,
  nameKey,
  deeplinkKey,
  colors,
  stacked,
  ...props
}) {
  nameKey = nameKey || 'name';
  deeplinkKey = deeplinkKey || 'deep_link';
  const transformedChartData = chartData.map((data) => {
    const transformedData = {};
    transformedData[nameKey] = data[nameKey];
    (Object.keys(data) || []).forEach((key) => {
      if (key !== nameKey && key !== deeplinkKey) {
        transformedData[key] = data[key].value;
      } else if (key === deeplinkKey) {
        transformedData[key] = data[key];
      }
    });
    return transformedData;
  });
  const { innerWidth: width, innerHeight: height } = window;
  const fillFor = (name) => (colors && colors[name]) || toColor(name);
  const stackId = stacked ? { stackId: nameKey } : {};
  const { handleDeeplink, setDeepLink, overrideHandleDeepLink } = props;
  if (!dataKeys && transformedChartData) {
    dataKeys = Object.keys(
      transformedChartData.reduce((names, item) => ({ ...names, ...item }), {})
    ).filter((key) => key !== nameKey && key !== deeplinkKey);
  }
  const dataFormatter = (number) => {
    if (number > 100000000) {
      return (number / 10000000).toString() + 'CR';
    } else if (number > 100000) {
      return (number / 100000).toString() + 'L';
    } else if (number > 1000) {
      return (number / 1000).toString() + 'K';
    } else {
      return number.toString();
    }
  };

  return (
    <div
      style={{
        paddingRight: '30px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <ResponsiveContainer width="95%" height={375}>
        <BarChart height={400} data={transformedChartData} barSize={20}>
          <defs>
            {Object.keys(linearGradients).map((gradient, index) => (
              <linearGradient
                id={gradient}
                x1="0"
                y1="0"
                x2="0"
                y2="100%"
                spreadMethod="reflect"
              >
                <stop offset="0" stopColor={linearGradients[gradient].color1} />
                <stop offset="1" stopColor={linearGradients[gradient].color2} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="2 2" />
          <XAxis dataKey={nameKey} />
          <YAxis tickFormatter={dataFormatter} />
          <Tooltip
            content={<CustomBarToolTip tooltipData={chartData} />}
            cursor={{ fill: '#f7f7f7' }}
          />
          {/* fill: "url(#gradientrgba(0,224,208,1))", dataKey: "deep_link", unit: undefined, formatter: undefined, name: "deep_link" */}
          <Legend
            payload={Object.keys(colors).map((item) => {
              return {
                inactive: false,
                dataKey: item,
                color: `url(#gradient${fillFor(item)})`,
                value: item,
                label: item,
              };
            })}
          />
          {dataKeys &&
            dataKeys.map((dataKey, index) => (
              <Bar
                key={dataKey}
                onClick={(barProps) =>
                  barProps.deep_link &&
                  (overrideHandleDeepLink ||
                    !barProps.apiType ||
                    barProps.apiType === 1
                    ? setDeepLink
                    : handleDeeplink)(
                    barProps.deep_link,
                    barProps.apiType,
                    props
                  )
                }
                dataKey={dataKey}
                barSize={(dataKeys || []).length < 4 ? 28 : undefined}
                fill={`url(#gradient${fillFor(dataKey)})`}
                cursor="pointer"
                {...stackId}
              />
            ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TableWidget({ chartData, columns, exportColumns, exportFileName }) {
  return (
    <>
      <TableList
        entities={chartData}
        columns={columns}
        exportFileName={exportFileName}
        exportColumns={exportColumns}
      />
    </>
  );
}

const defaultDefinition = {
  width: 700, // TODO: finalize
  height: 320,
};

function withInit(Component) {
  const { innerWidth: width, innerHeight: height } = window;
  return (props) => {
    const { chart_data, chart_data_url, ...widgetProps } = {
      ...defaultDefinition,
      ...props,
    };
    const [data, setData] = React.useState(chart_data);

    React.useEffect(() => {
      if (chart_data_url) {
        fetchChartData(deeplinkToURL(chart_data_url))
          .then(setData)
          .catch(console.error);
        return;
      }

      setData(chart_data);
    }, [chart_data, chart_data_url]);

    return (
      <>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#FFFFFF',
            borderRadius: '10px',
            boxShadow: '0px 2.5px 20px #0000002d',
            marginBottom: '10px',
            paddingTop: '20px',
            paddingBottom: '20px',
          }}
        >
          <Component {...widgetProps} chartData={data} />
        </div>
        <div
          style={{
            fontSize: '18px',
            color: '#748AA1',
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
        >
          {props.title}
        </div>
      </>
    );
  };
}

const BarChartWidgetInitialized = withInit(BarChartWidget);
const PieChartWidgetInitialized = withInit(PieChartWidget);
const TableWidgetInitialized = withInit(TableWidget);

export {
  BarChartWidgetInitialized as BarChartWidget,
  PieChartWidgetInitialized as PieChartWidget,
  TableWidgetInitialized as TableWidget,
};
